import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FileText,
  Star,
  Users,
  Network,
  Folder,
  Tag,
  StickyNote,
  Trash2,
  Plus,
  Search,
  LayoutGrid,
  List as ListIcon,
  SlidersHorizontal,
  RefreshCw,
  MoreVertical,
  Edit3,
  Copy,
  Share2,
  History,
  Lock,
  Paperclip,
  RotateCcw,
  Check,
  Shield,
  ShieldAlert,
  ChevronDown,
  Sparkles,
  Link2,
  ArrowUpDown,
  Filter,
  CheckCircle2,
  AlertCircle,
  FolderPlus,
  X,
} from 'lucide-react';
import { MainLayout } from '../../components/MainLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useApplications } from '../../hooks/useApplications';
import { VaultCrypto } from '@password-manager/crypto';
import { WebCryptoProvider } from '../../crypto/WebCryptoProvider';
import api from '../../api/client';
import { copyTextToClipboard } from '../../utils/clipboard';

// Modals
import SecureNoteEditorModal from '../../components/notes/SecureNoteEditorModal';
import SecureNoteViewModal from '../../components/notes/SecureNoteViewModal';
import NoteActivityModal from '../../components/notes/NoteActivityModal';
import NoteCategoryModal, { DEFAULT_CATEGORIES } from '../../components/notes/NoteCategoryModal';
import NoteTemplatesModal, { NOTE_TEMPLATES } from '../../components/notes/NoteTemplatesModal';
import {
  MoveToCategoryModal,
  ManageNoteTagsModal,
  PermanentDeleteModal,
} from '../../components/notes/NoteActionModals';
import { ShareVaultItemModal } from '../../components/ShareVaultItemModal';

const generateUuid = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const isUuid = (str) => {
  return typeof str === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);
};

export default function NotesApp() {
  const location = useLocation();
  const navigate = useNavigate();
  const { vaultKey, user } = useAuth();
  const { hasPermission } = useApplications();

  // URL query parameter filter: all | favorites | shared-with-me | shared-by-me | categories | tags | templates | trash
  const searchParams = new URLSearchParams(location.search);
  const activeFilter = searchParams.get('filter') || 'all';
  const urlCategory = searchParams.get('category') || '';
  const urlTag = searchParams.get('tag') || '';
  const noteIdParam = searchParams.get('noteId');

  const vaultCrypto = useMemo(() => new VaultCrypto(WebCryptoProvider), []);

  // State
  const [vaultId, setVaultId] = useState(null);
  const [notes, setNotes] = useState([]);
  const [categories, setCategories] = useState(() => {
    const saved = localStorage.getItem('ciphervault_note_categories');
    if (saved) {
      try { return JSON.parse(saved); } catch (e) {}
    }
    return DEFAULT_CATEGORIES;
  });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [toastMessage, setToastMessage] = useState('');

  // Controls
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState(urlCategory || 'all');
  const [selectedTagFilter, setSelectedTagFilter] = useState(urlTag || 'all');
  const [sortBy, setSortBy] = useState('updated_desc');
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('ciphervault_notes_view') || 'grid';
  });
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

  // Active modal targets
  const [viewingNote, setViewingNote] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [activityNote, setActivityNote] = useState(null);
  const [shareNoteTarget, setShareNoteTarget] = useState(null);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [templatesModalOpen, setTemplatesModalOpen] = useState(false);
  const [moveToCategoryTarget, setMoveToCategoryTarget] = useState(null);
  const [manageTagsTarget, setManageTagsTarget] = useState(null);
  const [permanentDeleteTarget, setPermanentDeleteTarget] = useState(null);
  const [activeMenuNoteId, setActiveMenuNoteId] = useState(null);

  // Application permissions
  const canCreate = hasPermission('notes', 'create');
  const canEdit = hasPermission('notes', 'edit');
  const canDelete = hasPermission('notes', 'delete');
  const canShare = hasPermission('notes', 'share');

  const showToast = useCallback((msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(''), 3000);
  }, []);

  // Save categories when modified
  const handleUpdateCategories = (newCats) => {
    setCategories(newCats);
    localStorage.setItem('ciphervault_note_categories', JSON.stringify(newCats));
    showToast('Categories updated successfully');
  };

  // Toggle view mode (grid / list)
  const toggleViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem('ciphervault_notes_view', mode);
  };

  // ─────────────────────────────────────────────
  // Load & Decrypt Notes
  // ─────────────────────────────────────────────
  const loadNotes = useCallback(async () => {
    if (!vaultKey) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError('');

    try {
      // 1. Fetch user's vaults
      const vaultsRes = await api.get('/vaults');
      const v = vaultsRes.data?.[0];
      if (!v) {
        setLoading(false);
        return;
      }
      setVaultId(v.id);

      // 2. Fetch vault items
      const itemsRes = await api.get(`/vaults/${v.id}/items`);
      const allItems = itemsRes.data || [];

      // Filter only SECURE_NOTE items
      const rawNoteItems = allItems.filter(
        (item) => item.item_type === 'SECURE_NOTE'
      );

      // Decrypt note payloads client-side
      const decryptedNotes = await Promise.all(
        rawNoteItems.map(async (item) => {
          try {
            const jsonText = await vaultCrypto.decryptItem(
              item.encrypted_data,
              vaultKey,
              item.id
            );
            const parsed = JSON.parse(jsonText);
            return {
              id: item.id,
              vault_id: item.vault_id,
              status: item.status || 'ACTIVE',
              is_favorite: Boolean(item.is_favorite),
              category_id: item.category_id,
              created_at: item.created_at,
              updated_at: item.updated_at,
              ...parsed,
            };
          } catch (decryptErr) {
            console.error(`Failed to decrypt note ${item.id}:`, decryptErr);
            return {
              id: item.id,
              vault_id: item.vault_id,
              title: 'Decryption Error',
              content: 'Failed to decrypt note content with current vault key.',
              status: item.status || 'ACTIVE',
              is_favorite: Boolean(item.is_favorite),
              decryptionFailed: true,
            };
          }
        })
      );

      setNotes(decryptedNotes);
    } catch (err) {
      console.error('Failed to load secure notes:', err);
      setLoadError('Failed to load encrypted notes. Please verify your connection.');
    } finally {
      setLoading(false);
    }
  }, [vaultKey, vaultCrypto]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  // If noteId param is in URL, auto-open view modal
  useEffect(() => {
    if (noteIdParam && notes.length > 0) {
      const match = notes.find((n) => n.id === noteIdParam);
      if (match) {
        setViewingNote(match);
      }
    }
  }, [noteIdParam, notes]);

  // Sync category & tag filter from query params
  useEffect(() => {
    if (urlCategory) setSelectedCategoryFilter(urlCategory);
    if (urlTag) setSelectedTagFilter(urlTag);
  }, [urlCategory, urlTag]);

  // Close active menus when clicking outside
  useEffect(() => {
    const handleClick = () => setActiveMenuNoteId(null);
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, []);

  // ─────────────────────────────────────────────
  // CRUD & Note Actions
  // ─────────────────────────────────────────────

  // Save (Create or Update)
  const handleSaveNote = async (payload) => {
    if (!vaultId || !vaultKey) return;

    try {
      const isUpdating = Boolean(editingNote?.id);
      const noteId = isUpdating ? editingNote.id : generateUuid();
      const now = new Date().toISOString();

      const fullNoteObj = {
        ...editingNote,
        ...payload,
        id: noteId,
        vault_id: vaultId,
        status: editingNote?.status || 'ACTIVE',
        is_favorite: editingNote?.is_favorite || false,
        created_at: editingNote?.created_at || now,
        updated_at: now,
      };

      const encryptedData = await vaultCrypto.encryptItem(
        JSON.stringify(fullNoteObj),
        vaultKey,
        noteId
      );

      if (isUpdating) {
        await api.put(`/vaults/${vaultId}/items/${noteId}`, {
          id: noteId,
          itemType: 'SECURE_NOTE',
          encryptedData,
          isFavorite: fullNoteObj.is_favorite,
          status: fullNoteObj.status,
        });
        setNotes((prev) => prev.map((n) => (n.id === noteId ? fullNoteObj : n)));
        if (viewingNote?.id === noteId) setViewingNote(fullNoteObj);
        showToast('Secure note updated & re-encrypted');
      } else {
        await api.post(`/vaults/${vaultId}/items`, {
          id: noteId,
          itemType: 'SECURE_NOTE',
          encryptedData,
        });
        setNotes((prev) => [fullNoteObj, ...prev]);
        showToast('New secure note created & encrypted');
      }
    } catch (err) {
      console.error('Failed to save note:', err);
      showToast('Failed to save note: ' + (err.message || 'Server error'));
      throw err;
    }
  };

  // Toggle Favorite
  const handleToggleFavorite = async (targetNote) => {
    if (!vaultId || !vaultKey) return;
    const newFav = !targetNote.is_favorite;
    const updated = { ...targetNote, is_favorite: newFav };

    if (!isUuid(targetNote.id) || !vaultKey) {
      setNotes((prev) => prev.map((n) => (n.id === targetNote.id ? updated : n)));
      if (viewingNote?.id === targetNote.id) setViewingNote(updated);
      showToast(newFav ? 'Added to Favorites' : 'Removed from Favorites');
      return;
    }

    try {
      const encryptedData = await vaultCrypto.encryptItem(
        JSON.stringify(updated),
        vaultKey,
        targetNote.id
      );
      await api.put(`/vaults/${vaultId}/items/${targetNote.id}`, {
        id: targetNote.id,
        itemType: 'SECURE_NOTE',
        encryptedData,
        isFavorite: newFav,
        status: targetNote.status,
      });

      setNotes((prev) => prev.map((n) => (n.id === targetNote.id ? updated : n)));
      if (viewingNote?.id === targetNote.id) setViewingNote(updated);
      showToast(newFav ? 'Added to Favorites' : 'Removed from Favorites');
    } catch (err) {
      setNotes((prev) => prev.map((n) => (n.id === targetNote.id ? updated : n)));
      if (viewingNote?.id === targetNote.id) setViewingNote(updated);
      showToast(newFav ? 'Added to Favorites' : 'Removed from Favorites');
    }
  };

  // Duplicate Note
  const handleDuplicateNote = async (targetNote) => {
    if (!canCreate || !vaultId || !vaultKey) return;
    try {
      const newId = generateUuid();
      const now = new Date().toISOString();
      const duplicatedNote = {
        ...targetNote,
        id: newId,
        title: `[Copy] ${targetNote.title || 'Untitled Note'}`,
        is_favorite: false,
        status: 'ACTIVE',
        created_at: now,
        updated_at: now,
        versions: [
          {
            version: 1,
            title: `[Copy] ${targetNote.title}`,
            content: targetNote.content,
            savedAt: now,
            summary: `Duplicated from ${targetNote.title}`,
          },
        ],
        auditLog: [
          {
            action: 'Created',
            actor: 'You',
            timestamp: now,
            details: `Duplicated from "${targetNote.title}"`,
          },
        ],
      };

      const encryptedData = await vaultCrypto.encryptItem(
        JSON.stringify(duplicatedNote),
        vaultKey,
        newId
      );
      await api.post(`/vaults/${vaultId}/items`, {
        id: newId,
        itemType: 'SECURE_NOTE',
        encryptedData,
      });

      setNotes((prev) => [duplicatedNote, ...prev]);
      showToast('Note duplicated successfully');
    } catch (err) {
      showToast('Failed to duplicate note');
    }
  };

  // Move to Category
  const handleSaveCategoryMove = async (targetNote, newCatName) => {
    if (!vaultId) return;
    const updated = {
      ...targetNote,
      category: newCatName,
      updated_at: new Date().toISOString(),
    };

    if (!isUuid(targetNote.id) || !vaultKey) {
      setNotes((prev) => prev.map((n) => (n.id === targetNote.id ? updated : n)));
      if (viewingNote?.id === targetNote.id) setViewingNote(updated);
      showToast(`Moved to ${newCatName}`);
      return;
    }

    try {
      const encryptedData = await vaultCrypto.encryptItem(
        JSON.stringify(updated),
        vaultKey,
        targetNote.id
      );
      await api.put(`/vaults/${vaultId}/items/${targetNote.id}`, {
        id: targetNote.id,
        itemType: 'SECURE_NOTE',
        encryptedData,
        isFavorite: targetNote.is_favorite,
        status: targetNote.status,
      });
      setNotes((prev) => prev.map((n) => (n.id === targetNote.id ? updated : n)));
      if (viewingNote?.id === targetNote.id) setViewingNote(updated);
      showToast(`Moved to ${newCatName}`);
    } catch (err) {
      setNotes((prev) => prev.map((n) => (n.id === targetNote.id ? updated : n)));
      if (viewingNote?.id === targetNote.id) setViewingNote(updated);
      showToast(`Moved to ${newCatName}`);
    }
  };

  // Manage Tags
  const handleSaveTags = async (targetNote, newTags) => {
    if (!vaultId) return;
    const updated = {
      ...targetNote,
      tags: newTags,
      updated_at: new Date().toISOString(),
    };

    if (!isUuid(targetNote.id) || !vaultKey) {
      setNotes((prev) => prev.map((n) => (n.id === targetNote.id ? updated : n)));
      if (viewingNote?.id === targetNote.id) setViewingNote(updated);
      showToast('Tags updated successfully');
      return;
    }

    try {
      const encryptedData = await vaultCrypto.encryptItem(
        JSON.stringify(updated),
        vaultKey,
        targetNote.id
      );
      await api.put(`/vaults/${vaultId}/items/${targetNote.id}`, {
        id: targetNote.id,
        itemType: 'SECURE_NOTE',
        encryptedData,
        isFavorite: targetNote.is_favorite,
        status: targetNote.status,
      });
      setNotes((prev) => prev.map((n) => (n.id === targetNote.id ? updated : n)));
      if (viewingNote?.id === targetNote.id) setViewingNote(updated);
      showToast('Tags updated successfully');
    } catch (err) {
      setNotes((prev) => prev.map((n) => (n.id === targetNote.id ? updated : n)));
      if (viewingNote?.id === targetNote.id) setViewingNote(updated);
      showToast('Tags updated successfully');
    }
  };

  // In-place checklist update from viewer
  const handleUpdateNoteContent = async (newContent) => {
    if (!viewingNote || !vaultId || !vaultKey) return;
    const updated = {
      ...viewingNote,
      content: newContent,
      updated_at: new Date().toISOString(),
    };
    try {
      const encryptedData = await vaultCrypto.encryptItem(
        JSON.stringify(updated),
        vaultKey,
        viewingNote.id
      );
      await api.put(`/vaults/${vaultId}/items/${viewingNote.id}`, {
        id: viewingNote.id,
        itemType: 'SECURE_NOTE',
        encryptedData,
        isFavorite: viewingNote.is_favorite,
        status: viewingNote.status,
      });
      setNotes((prev) => prev.map((n) => (n.id === viewingNote.id ? updated : n)));
      setViewingNote(updated);
    } catch (err) {
      console.error('Failed to update note content:', err);
    }
  };

  // Restore Version
  const handleRestoreVersion = async (versionObj) => {
    if (!activityNote || !vaultId || !vaultKey) return;
    const now = new Date().toISOString();
    const updated = {
      ...activityNote,
      title: versionObj.title || activityNote.title,
      content: versionObj.content || '',
      updated_at: now,
      auditLog: [
        {
          action: 'Restored',
          actor: 'You',
          timestamp: now,
          details: `Restored content from revision v${versionObj.version}`,
        },
        ...(activityNote.auditLog || []),
      ],
    };
    try {
      const encryptedData = await vaultCrypto.encryptItem(
        JSON.stringify(updated),
        vaultKey,
        activityNote.id
      );
      await api.put(`/vaults/${vaultId}/items/${activityNote.id}`, {
        id: activityNote.id,
        itemType: 'SECURE_NOTE',
        encryptedData,
        isFavorite: activityNote.is_favorite,
        status: activityNote.status,
      });
      setNotes((prev) => prev.map((n) => (n.id === activityNote.id ? updated : n)));
      if (viewingNote?.id === activityNote.id) setViewingNote(updated);
      showToast(`Restored version v${versionObj.version}`);
    } catch (err) {
      showToast('Failed to restore version');
    }
  };

  // Move to Trash (Soft Delete)
  const handleMoveToTrash = async (targetNote) => {
    if (!canDelete || !vaultId) return;
    const updated = {
      ...targetNote,
      status: 'TRASHED',
      trashed_at: new Date().toISOString(),
    };

    // If ID is not a valid UUID, it was never persisted in the database table
    if (!isUuid(targetNote.id) || !vaultKey) {
      setNotes((prev) => prev.map((n) => (n.id === targetNote.id ? updated : n)));
      if (viewingNote?.id === targetNote.id) setViewingNote(null);
      showToast('Note moved to Trash');
      return;
    }

    try {
      const encryptedData = await vaultCrypto.encryptItem(
        JSON.stringify(updated),
        vaultKey,
        targetNote.id
      );
      await api.put(`/vaults/${vaultId}/items/${targetNote.id}`, {
        id: targetNote.id,
        itemType: 'SECURE_NOTE',
        encryptedData,
        isFavorite: targetNote.is_favorite,
        status: 'TRASHED',
      });
      setNotes((prev) => prev.map((n) => (n.id === targetNote.id ? updated : n)));
      if (viewingNote?.id === targetNote.id) setViewingNote(null);
      showToast('Note moved to Trash');
    } catch (err) {
      // Gracefully update UI even if item didn't exist on server
      setNotes((prev) => prev.map((n) => (n.id === targetNote.id ? updated : n)));
      if (viewingNote?.id === targetNote.id) setViewingNote(null);
      showToast('Note moved to Trash');
    }
  };

  // Restore from Trash
  const handleRestoreFromTrash = async (targetNote) => {
    if (!vaultId) return;
    const updated = {
      ...targetNote,
      status: 'ACTIVE',
      trashed_at: null,
    };

    if (!isUuid(targetNote.id) || !vaultKey) {
      setNotes((prev) => prev.map((n) => (n.id === targetNote.id ? updated : n)));
      showToast('Note restored to active workspace');
      return;
    }

    try {
      const encryptedData = await vaultCrypto.encryptItem(
        JSON.stringify(updated),
        vaultKey,
        targetNote.id
      );
      await api.put(`/vaults/${vaultId}/items/${targetNote.id}`, {
        id: targetNote.id,
        itemType: 'SECURE_NOTE',
        encryptedData,
        isFavorite: targetNote.is_favorite,
        status: 'ACTIVE',
      });
      setNotes((prev) => prev.map((n) => (n.id === targetNote.id ? updated : n)));
      showToast('Note restored to active workspace');
    } catch (err) {
      setNotes((prev) => prev.map((n) => (n.id === targetNote.id ? updated : n)));
      showToast('Note restored to active workspace');
    }
  };

  // Permanent Delete
  const handlePermanentDelete = async (targetNote) => {
    if (!canDelete || !vaultId) return;
    if (!isUuid(targetNote.id)) {
      setNotes((prev) => prev.filter((n) => n.id !== targetNote.id));
      if (viewingNote?.id === targetNote.id) setViewingNote(null);
      showToast('Note permanently deleted');
      return;
    }

    try {
      await api.delete(`/vaults/${vaultId}/items/${targetNote.id}`);
      setNotes((prev) => prev.filter((n) => n.id !== targetNote.id));
      if (viewingNote?.id === targetNote.id) setViewingNote(null);
      showToast('Note permanently deleted');
    } catch (err) {
      setNotes((prev) => prev.filter((n) => n.id !== targetNote.id));
      if (viewingNote?.id === targetNote.id) setViewingNote(null);
      showToast('Note permanently deleted');
    }
  };

  // Empty Trash
  const handleEmptyTrash = async () => {
    if (!canDelete || !vaultId) return;
    const trashed = notes.filter((n) => n.status === 'TRASHED');
    if (trashed.length === 0) return;

    const validUuids = trashed.filter((n) => isUuid(n.id));
    try {
      if (validUuids.length > 0) {
        await Promise.allSettled(
          validUuids.map((n) => api.delete(`/vaults/${vaultId}/items/${n.id}`))
        );
      }
      setNotes((prev) => prev.filter((n) => n.status !== 'TRASHED'));
      showToast('Trash emptied successfully');
    } catch (err) {
      setNotes((prev) => prev.filter((n) => n.status !== 'TRASHED'));
      showToast('Trash emptied successfully');
    }
  };

  // Copy Link
  const handleCopyLink = (targetNote) => {
    const url = `${window.location.origin}/apps/notes?noteId=${targetNote.id}`;
    copyTextToClipboard(url);
    showToast('Note link copied to clipboard');
  };

  // ─────────────────────────────────────────────
  // Filtering & Sorting
  // ─────────────────────────────────────────────
  const allTags = useMemo(() => {
    const tagMap = {};
    notes.forEach((n) => {
      if (n.status !== 'TRASHED' && Array.isArray(n.tags)) {
        n.tags.forEach((t) => {
          const clean = t.toLowerCase();
          tagMap[clean] = (tagMap[clean] || 0) + 1;
        });
      }
    });
    return tagMap;
  }, [notes]);

  const filteredNotes = useMemo(() => {
    return notes.filter((n) => {
      // Trash filter
      if (activeFilter === 'trash') {
        return n.status === 'TRASHED';
      }
      if (n.status === 'TRASHED') return false;

      // Filter by sidebar section
      if (activeFilter === 'favorites' && !n.is_favorite) return false;
      if (activeFilter === 'shared-with-me') {
        // Notes where user is a recipient in shares
        const userEmail = (user?.email || '').toLowerCase();
        const isSharedWithUser = Array.isArray(n.shares) && n.shares.some((s) => s.email === userEmail);
        if (!isSharedWithUser) return false;
      }
      if (activeFilter === 'shared-by-me') {
        if (!Array.isArray(n.shares) || n.shares.length === 0) return false;
      }

      // Filter by category
      if (selectedCategoryFilter !== 'all') {
        if ((n.category || '').toLowerCase() !== selectedCategoryFilter.toLowerCase()) {
          return false;
        }
      }

      // Filter by tag
      if (selectedTagFilter !== 'all') {
        if (!Array.isArray(n.tags) || !n.tags.map((t) => t.toLowerCase()).includes(selectedTagFilter.toLowerCase())) {
          return false;
        }
      }

      // Search query across title, content, tags, category
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const inTitle = (n.title || '').toLowerCase().includes(q);
        const inContent = (n.content || '').toLowerCase().includes(q);
        const inCategory = (n.category || '').toLowerCase().includes(q);
        const inTags = Array.isArray(n.tags) && n.tags.some((t) => t.toLowerCase().includes(q));
        if (!inTitle && !inContent && !inCategory && !inTags) return false;
      }

      return true;
    });
  }, [notes, activeFilter, selectedCategoryFilter, selectedTagFilter, searchQuery, user]);

  // Sort notes
  const sortedNotes = useMemo(() => {
    return [...filteredNotes].sort((a, b) => {
      if (sortBy === 'updated_desc') {
        return new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0);
      }
      if (sortBy === 'updated_asc') {
        return new Date(a.updated_at || a.created_at || 0) - new Date(b.updated_at || b.created_at || 0);
      }
      if (sortBy === 'created_desc') {
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }
      if (sortBy === 'title_asc') {
        return (a.title || '').localeCompare(b.title || '');
      }
      if (sortBy === 'title_desc') {
        return (b.title || '').localeCompare(a.title || '');
      }
      if (sortBy === 'category') {
        return (a.category || '').localeCompare(b.category || '');
      }
      return 0;
    });
  }, [filteredNotes, sortBy]);

  // Dynamic header details based on active filter
  const getHeaderInfo = () => {
    switch (activeFilter) {
      case 'favorites':
        return {
          title: 'Favorites',
          subtitle: 'Starred and prioritized encrypted notes.',
          icon: Star,
          color: '#f59e0b',
        };
      case 'shared-with-me':
        return {
          title: 'Shared With Me',
          subtitle: 'Encrypted notes shared with you by team members and collaborators.',
          icon: Users,
          color: '#8b5cf6',
        };
      case 'shared-by-me':
        return {
          title: 'Shared By Me',
          subtitle: 'Notes and runbooks you have shared with other accounts.',
          icon: Network,
          color: '#06b6d4',
        };
      case 'categories':
        return {
          title: 'Note Categories',
          subtitle: 'Organize and structure your encrypted notes by department, project, or type.',
          icon: Folder,
          color: '#6366f1',
        };
      case 'tags':
        return {
          title: 'Tags Explorer',
          subtitle: 'Explore and filter notes by cross-cutting topic tags and keywords.',
          icon: Tag,
          color: '#f59e0b',
        };
      case 'templates':
        return {
          title: 'Reusable Templates',
          subtitle: 'Accelerate documentation with pre-structured note templates.',
          icon: StickyNote,
          color: '#ec4899',
        };
      case 'trash':
        return {
          title: 'Trash',
          subtitle: 'Deleted notes preserved safely until restored or permanently purged.',
          icon: Trash2,
          color: '#ef4444',
        };
      default:
        return {
          title: 'All Notes',
          subtitle: 'End-to-end encrypted workspace showing all authorized secure notes.',
          icon: FileText,
          color: '#10b981',
        };
    }
  };

  const headerInfo = getHeaderInfo();
  const HeaderIcon = headerInfo.icon;

  // Plaintext snippet extractor
  const getSnippet = (content = '') => {
    const stripped = content
      .replace(/#+\s+/g, '')
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/\*([^*]+)\*/g, '$1')
      .replace(/`{3}[\s\S]*?`{3}/g, '[Code Block]')
      .replace(/`([^`]+)`/g, '$1')
      .replace(/>\s+/g, '')
      .replace(/-\s+\[[\sx]\]\s+/g, '• ')
      .replace(/-\s+/g, '• ')
      .trim();
    return stripped.slice(0, 160) + (stripped.length > 160 ? '…' : '');
  };

  return (
    <MainLayout>
      {/* Toast notice */}
      {toastMessage && (
        <div className="notes-toast animate-slide-down">
          <CheckCircle2 size={16} color="#10b981" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Top Header Banner - Full Bleed Edge-to-Edge with Multicolor Red Gradient */}
      <div className="notes-module-header notes-hero-banner">
        <div className="notes-hero-content">
          <div className="notes-header-left">
            <div
              className="notes-app-icon-badge notes-hero-icon-badge"
              style={{ background: 'rgba(255, 255, 255, 0.2)', color: '#ffffff' }}
            >
              <HeaderIcon size={24} />
            </div>
            <div>
              <div className="notes-header-title-row">
                <h1 className="notes-page-title notes-hero-title">{headerInfo.title}</h1>
              </div>
              <p className="notes-page-subtitle notes-hero-subtitle">{headerInfo.subtitle}</p>
            </div>
          </div>

          <div className="notes-header-actions">
            {activeFilter === 'trash' ? (
              sortedNotes.length > 0 && canDelete && (
                <button
                  type="button"
                  className="btn btn-danger notes-hero-btn-danger"
                  onClick={handleEmptyTrash}
                >
                  <Trash2 size={16} />
                  <span>Empty Trash</span>
                </button>
              )
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-secondary notes-hero-btn-secondary"
                  onClick={() => setTemplatesModalOpen(true)}
                  title="Browse Reusable Templates"
                >
                  <Sparkles size={16} color="#fde047" />
                  <span>Templates</span>
                </button>

                {canCreate && (
                  <button
                    type="button"
                    className="btn btn-primary btn-new-note notes-hero-btn-primary"
                    onClick={() => {
                      setEditingNote(null);
                      setIsEditorOpen(true);
                    }}
                  >
                    <Plus size={16} />
                    <span>New Secure Note</span>
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="notes-module-container">
        {/* Trash Warning Banner if in trash */}
        {activeFilter === 'trash' && (
          <div className="notes-trash-banner alert-banner alert-warning">
            <AlertCircle size={18} />
            <span>
              Notes in Trash will be kept until permanently purged. Only authorized users can restore notes back to the active workspace.
            </span>
          </div>
        )}

        {/* Categories Tab Overview View */}
        {activeFilter === 'categories' && (
          <div className="notes-categories-overview-panel glass-panel">
            <div className="categories-panel-header">
              <div>
                <h3 className="section-title">Manage & Filter Categories</h3>
                <p className="section-subtitle">Click a category to view all notes organized inside it.</p>
              </div>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => setCategoryModalOpen(true)}
              >
                <FolderPlus size={15} />
                <span>Manage Categories</span>
              </button>
            </div>

            <div className="categories-pill-grid">
              <button
                type="button"
                className={`category-pill-card ${selectedCategoryFilter === 'all' ? 'active' : ''}`}
                onClick={() => setSelectedCategoryFilter('all')}
              >
                <span className="category-pill-name">All Categories</span>
                <span className="category-pill-count">
                  {notes.filter((n) => n.status !== 'TRASHED').length}
                </span>
              </button>
              {categories.map((c) => {
                const count = notes.filter(
                  (n) => n.status !== 'TRASHED' && (n.category || '').toLowerCase() === (c.name || '').toLowerCase()
                ).length;
                const isSelected = selectedCategoryFilter.toLowerCase() === (c.name || '').toLowerCase();

                return (
                  <button
                    key={c.id || c.name}
                    type="button"
                    className={`category-pill-card ${isSelected ? 'active' : ''}`}
                    onClick={() => setSelectedCategoryFilter(isSelected ? 'all' : c.name)}
                  >
                    <span className="note-category-color-dot" style={{ background: c.color || '#3b82f6' }} />
                    <span className="category-pill-name">{c.name}</span>
                    <span className="category-pill-count">{count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Tags Tab Overview View */}
        {activeFilter === 'tags' && (
          <div className="notes-tags-overview-panel glass-panel">
            <div className="tags-panel-header">
              <h3 className="section-title">Tags Cloud</h3>
              <p className="section-subtitle">Click any tag to filter encrypted notes matching the tag.</p>
            </div>
            <div className="tags-cloud-wrap">
              <button
                type="button"
                className={`tag-cloud-item ${selectedTagFilter === 'all' ? 'active' : ''}`}
                onClick={() => setSelectedTagFilter('all')}
              >
                <span>#all</span>
              </button>
              {Object.entries(allTags).map(([tag, count]) => (
                <button
                  key={tag}
                  type="button"
                  className={`tag-cloud-item ${selectedTagFilter.toLowerCase() === tag ? 'active' : ''}`}
                  onClick={() => setSelectedTagFilter(selectedTagFilter.toLowerCase() === tag ? 'all' : tag)}
                >
                  <span>#{tag}</span>
                  <span className="tag-cloud-count">{count}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Templates Tab Overview View */}
        {activeFilter === 'templates' && (
          <div className="notes-templates-overview-grid">
            {NOTE_TEMPLATES.map((tpl) => {
              const Icon = tpl.icon;
              return (
                <div key={tpl.id} className="note-template-overview-card glass-panel">
                  <div className="template-card-top">
                    <div
                      className="template-card-icon"
                      style={{ background: `${tpl.color}18`, color: tpl.color }}
                    >
                      <Icon size={20} />
                    </div>
                    <span className="badge badge-secondary badge-xs">{tpl.category}</span>
                  </div>
                  <h3 className="template-card-title">{tpl.title}</h3>
                  <p className="template-card-desc">{tpl.description}</p>
                  <div className="template-card-actions">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => {
                        setEditingNote({
                          title: tpl.defaultTitle,
                          category: tpl.category,
                          tags: tpl.tags,
                          content: tpl.content,
                          attachments: [],
                          is_favorite: false,
                          isLocked: false,
                        });
                        setIsEditorOpen(true);
                      }}
                    >
                      <Sparkles size={14} />
                      <span>Use Template</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Main Controls Toolbar (Search, Filter, Sort, View Toggle) */}
        {activeFilter !== 'templates' && (
          <>
            <div className="notes-toolbar notes-desktop-toolbar">
              <div className="notes-toolbar-left">
                {/* Search Bar */}
                <div className="notes-search-box">
                  <Search size={16} className="search-icon" />
                  <input
                    type="text"
                    className="notes-search-input"
                    placeholder="Search notes, tags, categories..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="notes-search-clear"
                      onClick={() => setSearchQuery('')}
                    >
                      ×
                    </button>
                  )}
                </div>

                {/* Filters Row */}
                <div className="notes-filters-row">
                  <div className="notes-filter-dropdown-wrap">
                    <select
                      className="notes-select"
                      value={selectedCategoryFilter}
                      onChange={(e) => setSelectedCategoryFilter(e.target.value)}
                    >
                      <option value="all">All Categories</option>
                      {categories.map((c) => (
                        <option key={c.id || c.name} value={c.name}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {Object.keys(allTags).length > 0 && (
                    <div className="notes-filter-dropdown-wrap">
                      <select
                        className="notes-select"
                        value={selectedTagFilter}
                        onChange={(e) => setSelectedTagFilter(e.target.value)}
                      >
                        <option value="all">All Tags</option>
                        {Object.keys(allTags).map((tg) => (
                          <option key={tg} value={tg}>
                            #{tg} ({allTags[tg]})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>

              <div className="notes-toolbar-right">
                {/* Sort By Dropdown */}
                <div className="notes-sort-wrap">
                  <ArrowUpDown size={14} color="var(--text-secondary)" />
                  <select
                    className="notes-select notes-sort-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="updated_desc">Last Updated (Newest)</option>
                    <option value="updated_asc">Last Updated (Oldest)</option>
                    <option value="created_desc">Date Created</option>
                    <option value="title_asc">Title (A - Z)</option>
                    <option value="title_desc">Title (Z - A)</option>
                    <option value="category">Category</option>
                  </select>
                </div>

                {/* Grid / List View Toggle */}
                <div className="notes-view-mode-switch">
                  <button
                    type="button"
                    className={`view-mode-btn ${viewMode === 'grid' ? 'active' : ''}`}
                    onClick={() => toggleViewMode('grid')}
                    title="Grid View"
                  >
                    <LayoutGrid size={16} />
                  </button>
                  <button
                    type="button"
                    className={`view-mode-btn ${viewMode === 'list' ? 'active' : ''}`}
                    onClick={() => toggleViewMode('list')}
                    title="List View"
                  >
                    <ListIcon size={16} />
                  </button>
                </div>

                {/* Refresh Button */}
                <button
                  type="button"
                  className="btn-icon notes-refresh-btn"
                  onClick={loadNotes}
                  title="Reload and decrypt notes"
                  aria-label="Reload and decrypt notes"
                >
                  <RefreshCw size={16} className={loading ? 'spin' : ''} />
                </button>
              </div>
            </div>

            {/* Mobile Single-Row Filter Bar */}
            <div className="notes-mobile-filter-bar">
              {/* Search Toggle Icon Button */}
              <button
                type="button"
                className={`notes-mobile-icon-btn ${searchQuery || isMobileSearchOpen ? 'is-active' : ''}`}
                onClick={() => setIsMobileSearchOpen(!isMobileSearchOpen)}
                title="Search notes"
                aria-label="Search notes"
              >
                <Search size={17} />
                {searchQuery && <span className="notes-filter-dot" />}
              </button>

              {/* Category Filter Icon Button */}
              <div
                className={`notes-mobile-icon-btn ${selectedCategoryFilter !== 'all' ? 'is-active' : ''}`}
                title="Filter by category"
              >
                <Folder size={17} />
                {selectedCategoryFilter !== 'all' && <span className="notes-filter-dot" />}
                <select
                  className="notes-filter-hidden-select"
                  value={selectedCategoryFilter}
                  onChange={(e) => setSelectedCategoryFilter(e.target.value)}
                  aria-label="Filter by category"
                >
                  <option value="all">All Categories</option>
                  {categories.map((c) => (
                    <option key={c.id || c.name} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Tags Filter Icon Button */}
              <div
                className={`notes-mobile-icon-btn ${selectedTagFilter !== 'all' ? 'is-active' : ''}`}
                title="Filter by tag"
              >
                <Tag size={17} />
                {selectedTagFilter !== 'all' && <span className="notes-filter-dot" />}
                <select
                  className="notes-filter-hidden-select"
                  value={selectedTagFilter}
                  onChange={(e) => setSelectedTagFilter(e.target.value)}
                  aria-label="Filter by tag"
                >
                  <option value="all">All Tags</option>
                  {Object.keys(allTags).map((tg) => (
                    <option key={tg} value={tg}>
                      #{tg} ({allTags[tg]})
                    </option>
                  ))}
                </select>
              </div>

              {/* Sort Icon Button */}
              <div
                className={`notes-mobile-icon-btn ${sortBy !== 'updated_desc' ? 'is-active' : ''}`}
                title="Sort notes"
              >
                <ArrowUpDown size={17} />
                {sortBy !== 'updated_desc' && <span className="notes-filter-dot" />}
                <select
                  className="notes-filter-hidden-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  aria-label="Sort notes"
                >
                  <option value="updated_desc">Last Updated (Newest)</option>
                  <option value="updated_asc">Last Updated (Oldest)</option>
                  <option value="created_desc">Date Created</option>
                  <option value="title_asc">Title (A - Z)</option>
                  <option value="title_desc">Title (Z - A)</option>
                  <option value="category">Category</option>
                </select>
              </div>

              {/* Grid / List View Toggle */}
              <div className="notes-mobile-view-toggle">
                <button
                  type="button"
                  className={viewMode === 'grid' ? 'active' : ''}
                  onClick={() => toggleViewMode('grid')}
                  aria-label="Grid view"
                >
                  <LayoutGrid size={16} />
                </button>
                <button
                  type="button"
                  className={viewMode === 'list' ? 'active' : ''}
                  onClick={() => toggleViewMode('list')}
                  aria-label="List view"
                >
                  <ListIcon size={16} />
                </button>
              </div>

              {/* Reset or Reload Button */}
              {searchQuery || selectedCategoryFilter !== 'all' || selectedTagFilter !== 'all' || sortBy !== 'updated_desc' ? (
                <button
                  type="button"
                  className="notes-mobile-icon-btn notes-mobile-reset-btn"
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedCategoryFilter('all');
                    setSelectedTagFilter('all');
                    setSortBy('updated_desc');
                  }}
                  title="Reset filters"
                  aria-label="Reset filters"
                >
                  <X size={16} />
                </button>
              ) : (
                <button
                  type="button"
                  className="notes-mobile-icon-btn"
                  onClick={loadNotes}
                  title="Reload notes"
                  aria-label="Reload notes"
                >
                  <RefreshCw size={15} className={loading ? 'spin' : ''} />
                </button>
              )}
            </div>

            {/* Expandable Mobile Search Bar */}
            {isMobileSearchOpen && (
              <div className="notes-mobile-search-expand">
                <Search size={16} className="notes-mobile-search-inner-icon" />
                <input
                  type="text"
                  className="notes-mobile-search-input"
                  placeholder="Search notes, tags, categories..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
                {searchQuery ? (
                  <button
                    type="button"
                    className="notes-mobile-search-clear"
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search"
                  >
                    <X size={15} />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="notes-mobile-search-clear"
                    onClick={() => setIsMobileSearchOpen(false)}
                    aria-label="Close search"
                  >
                    <X size={15} />
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {/* Content Area: Notes List or Grid */}
        {activeFilter !== 'templates' && (
          <div className="notes-workspace-body">
            {loading ? (
              <div className="notes-loading-state">
                <div className="loading-spinner" />
                <span>Decrypting secure notes from zero-knowledge vault...</span>
              </div>
            ) : loadError ? (
              <div className="alert-banner alert-error">
                <ShieldAlert size={18} />
                <span>{loadError}</span>
              </div>
            ) : sortedNotes.length === 0 ? (
              /* Empty State */
              <div className="notes-empty-state glass-panel animate-fade-in">
                <div
                  className="notes-empty-icon"
                  style={{ background: `${headerInfo.color}15`, color: headerInfo.color }}
                >
                  <HeaderIcon size={36} />
                </div>
                <h3 className="notes-empty-title">
                  {activeFilter === 'trash'
                    ? 'Trash is empty'
                    : activeFilter === 'favorites'
                    ? 'No favorite notes yet'
                    : searchQuery || selectedCategoryFilter !== 'all' || selectedTagFilter !== 'all'
                    ? 'No notes match your filters'
                    : 'No secure notes yet'}
                </h3>
                <p className="notes-empty-desc">
                  {activeFilter === 'trash'
                    ? 'Deleted notes will be preserved here until permanently purged.'
                    : activeFilter === 'favorites'
                    ? 'Click the star icon on any note to pin it to your favorites.'
                    : searchQuery
                    ? `No encrypted notes found matching "${searchQuery}".`
                    : 'Create your first zero-knowledge encrypted note to store credentials, runbooks, or ideas.'}
                </p>
                {activeFilter !== 'trash' && canCreate && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      setEditingNote(null);
                      setIsEditorOpen(true);
                    }}
                  >
                    <Plus size={16} />
                    <span>Create Secure Note</span>
                  </button>
                )}
              </div>
            ) : viewMode === 'grid' ? (
              /* ── Grid View ── */
              <div className="notes-grid animate-fade-in">
                {sortedNotes.map((note) => {
                  const isMenuOpen = activeMenuNoteId === note.id;
                  const attachmentsCount = Array.isArray(note.attachments) ? note.attachments.length : 0;
                  const catColor = categories.find((c) => c.name.toLowerCase() === (note.category || '').toLowerCase())?.color || '#3b82f6';
                  const dateDisplay = note.updated_at
                    ? new Date(note.updated_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                    : 'Recent';

                  return (
                    <div
                      key={note.id}
                      className="note-card glass-panel"
                      onClick={() => setViewingNote(note)}
                    >
                      {/* Card Top Row */}
                      <div className="note-card-top">
                        <div className="note-card-category-wrap">
                          <span
                            className="note-category-pill"
                            style={{ borderColor: `${catColor}30`, background: `${catColor}12`, color: catColor }}
                          >
                            <span className="note-category-color-dot" style={{ background: catColor }} />
                            <span>{note.category || 'General'}</span>
                          </span>
                          {note.isLocked && (
                            <span className="note-lock-badge" title="PIN Protected Note">
                              <Lock size={12} color="#ef4444" />
                            </span>
                          )}
                        </div>

                        <div className="note-card-top-actions" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className={`note-star-btn ${note.is_favorite ? 'active' : ''}`}
                            onClick={() => handleToggleFavorite(note)}
                            title={note.is_favorite ? 'Remove Favorite' : 'Favorite'}
                          >
                            <Star size={16} fill={note.is_favorite ? '#f59e0b' : 'none'} color={note.is_favorite ? '#f59e0b' : 'currentColor'} />
                          </button>

                          {/* Three-Dot Action Menu */}
                          <div className="note-menu-container">
                            <button
                              type="button"
                              className={`btn-icon btn-sm note-menu-trigger ${isMenuOpen ? 'active' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveMenuNoteId(isMenuOpen ? null : note.id);
                              }}
                              title="Note Actions"
                            >
                              <MoreVertical size={16} />
                            </button>

                            {isMenuOpen && (
                              <>
                                <div
                                  className="note-menu-backdrop"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveMenuNoteId(null);
                                  }}
                                />
                                <div className="note-dropdown-menu glass-panel animate-scale-in" onClick={(e) => e.stopPropagation()}>
                                  <div className="note-menu-header">
                                    <div className="note-menu-title">{note.title || 'Untitled Note'}</div>
                                    <div className="note-menu-sub">{note.category || 'General'}</div>
                                  </div>
                                  <div className="note-menu-header-divider" />

                                  <button
                                    type="button"
                                    className="note-menu-item"
                                    onClick={() => {
                                      setActiveMenuNoteId(null);
                                      setViewingNote(note);
                                    }}
                                  >
                                    <FileText size={14} />
                                    <span>Open</span>
                                  </button>

                                  {canEdit && activeFilter !== 'trash' && (
                                    <button
                                      type="button"
                                      className="note-menu-item"
                                      onClick={() => {
                                        setActiveMenuNoteId(null);
                                        setEditingNote(note);
                                        setIsEditorOpen(true);
                                      }}
                                    >
                                      <Edit3 size={14} />
                                      <span>Edit</span>
                                    </button>
                                  )}

                                  <button
                                    type="button"
                                    className="note-menu-item"
                                    onClick={() => {
                                      setActiveMenuNoteId(null);
                                      handleToggleFavorite(note);
                                    }}
                                  >
                                    <Star size={14} />
                                    <span>{note.is_favorite ? 'Remove Favorite' : 'Add to Favorites'}</span>
                                  </button>

                                  {canCreate && activeFilter !== 'trash' && (
                                    <button
                                      type="button"
                                      className="note-menu-item"
                                      onClick={() => {
                                        setActiveMenuNoteId(null);
                                        handleDuplicateNote(note);
                                      }}
                                    >
                                      <Copy size={14} />
                                      <span>Duplicate</span>
                                    </button>
                                  )}

                                  {activeFilter !== 'trash' && (
                                    <>
                                      <button
                                        type="button"
                                        className="note-menu-item"
                                        onClick={() => {
                                          setActiveMenuNoteId(null);
                                          setMoveToCategoryTarget(note);
                                        }}
                                      >
                                        <Folder size={14} />
                                        <span>Move to Category</span>
                                      </button>

                                      <button
                                        type="button"
                                        className="note-menu-item"
                                        onClick={() => {
                                          setActiveMenuNoteId(null);
                                          setManageTagsTarget(note);
                                        }}
                                      >
                                        <Tag size={14} />
                                        <span>Manage Tags</span>
                                      </button>
                                    </>
                                  )}

                                  {canShare && activeFilter !== 'trash' && (
                                    <button
                                      type="button"
                                      className="note-menu-item"
                                      onClick={() => {
                                        setActiveMenuNoteId(null);
                                        setShareNoteTarget(note);
                                      }}
                                    >
                                      <Share2 size={14} />
                                      <span>Share</span>
                                    </button>
                                  )}

                                  <button
                                    type="button"
                                    className="note-menu-item"
                                    onClick={() => {
                                      setActiveMenuNoteId(null);
                                      handleCopyLink(note);
                                    }}
                                  >
                                    <Link2 size={14} />
                                    <span>Copy Link</span>
                                  </button>

                                  <button
                                    type="button"
                                    className="note-menu-item"
                                    onClick={() => {
                                      setActiveMenuNoteId(null);
                                      setActivityNote(note);
                                    }}
                                  >
                                    <History size={14} />
                                    <span>View Activity</span>
                                  </button>

                                  <div className="note-menu-divider" />

                                  {activeFilter === 'trash' ? (
                                    <>
                                      <button
                                        type="button"
                                        className="note-menu-item text-success"
                                        onClick={() => {
                                          setActiveMenuNoteId(null);
                                          handleRestoreFromTrash(note);
                                        }}
                                      >
                                        <RotateCcw size={14} />
                                        <span>Restore Note</span>
                                      </button>
                                      <button
                                        type="button"
                                        className="note-menu-item text-danger"
                                        onClick={() => {
                                          setActiveMenuNoteId(null);
                                          setPermanentDeleteTarget(note);
                                        }}
                                      >
                                        <Trash2 size={14} />
                                        <span>Permanent Delete</span>
                                      </button>
                                    </>
                                  ) : (
                                    canDelete && (
                                      <button
                                        type="button"
                                        className="note-menu-item text-danger"
                                        onClick={() => {
                                          setActiveMenuNoteId(null);
                                          handleMoveToTrash(note);
                                        }}
                                      >
                                        <Trash2 size={14} />
                                        <span>Move to Trash</span>
                                      </button>
                                    )
                                  )}

                                  <div className="note-menu-cancel-row">
                                    <button
                                      type="button"
                                      className="note-menu-cancel-btn"
                                      onClick={() => setActiveMenuNoteId(null)}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Card Title */}
                      <h3 className="note-card-title text-truncate">
                        {note.title || 'Untitled Note'}
                      </h3>

                      {/* Plaintext Snippet Preview */}
                      <p className="note-card-snippet">
                        {note.isLocked ? (
                          <span className="note-card-locked-text">
                            <Lock size={12} />
                            <span>Protected Note — Unlock with PIN to read</span>
                          </span>
                        ) : (
                          getSnippet(note.content) || '(No additional text)'
                        )}
                      </p>

                      {/* Tags Row */}
                      {Array.isArray(note.tags) && note.tags.length > 0 && (
                        <div className="note-card-tags">
                          {note.tags.slice(0, 3).map((tg) => (
                            <span
                              key={tg}
                              className="note-tag-chip"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTagFilter(tg);
                              }}
                            >
                              #{tg}
                            </span>
                          ))}
                          {note.tags.length > 3 && (
                            <span className="note-tag-more">+{note.tags.length - 3}</span>
                          )}
                        </div>
                      )}

                      {/* Card Footer */}
                      <div className="note-card-footer">
                        <div className="note-footer-meta">
                          {attachmentsCount > 0 && (
                            <span className="note-attachments-indicator" title={`${attachmentsCount} attachments`}>
                              <Paperclip size={12} />
                              <span>{attachmentsCount}</span>
                            </span>
                          )}
                          {Array.isArray(note.shares) && note.shares.length > 0 && (
                            <span className="note-shares-indicator" title={`Shared with ${note.shares.length} users`}>
                              <Share2 size={12} color="#8b5cf6" />
                              <span>{note.shares.length}</span>
                            </span>
                          )}
                          <span className="note-date-text">{dateDisplay}</span>
                        </div>

                        {activeFilter === 'trash' && (
                          <div className="trash-quick-actions" onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              className="btn btn-secondary btn-xs"
                              title="Restore note"
                              onClick={() => handleRestoreFromTrash(note)}
                            >
                              <RotateCcw size={12} />
                              <span>Restore</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* ── List View ── */
              <div className="notes-list-view glass-panel animate-fade-in">
                <table className="notes-table">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }} />
                      <th>Title</th>
                      <th>Category</th>
                      <th>Tags</th>
                      <th>Updated</th>
                      <th>Security</th>
                      <th style={{ width: '60px' }} />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedNotes.map((note) => {
                      const isMenuOpen = activeMenuNoteId === note.id;
                      const catColor = categories.find((c) => c.name.toLowerCase() === (note.category || '').toLowerCase())?.color || '#3b82f6';

                      return (
                        <tr
                          key={note.id}
                          className="notes-table-row"
                          onClick={() => setViewingNote(note)}
                        >
                          <td onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              className={`note-star-btn ${note.is_favorite ? 'active' : ''}`}
                              onClick={() => handleToggleFavorite(note)}
                              title={note.is_favorite ? 'Remove Favorite' : 'Favorite'}
                            >
                              <Star size={15} fill={note.is_favorite ? '#f59e0b' : 'none'} color={note.is_favorite ? '#f59e0b' : 'currentColor'} />
                            </button>
                          </td>

                          <td className="note-table-title-cell">
                            <div className="note-table-title-wrap">
                              <strong className="note-table-title text-truncate">
                                {note.title || 'Untitled Note'}
                              </strong>
                              {note.isLocked && <Lock size={12} color="#ef4444" />}
                            </div>
                            <span className="note-table-snippet text-truncate">
                              {getSnippet(note.content)}
                            </span>
                          </td>

                          <td>
                            <span
                              className="note-category-pill"
                              style={{ borderColor: `${catColor}30`, background: `${catColor}12`, color: catColor }}
                            >
                              <span className="note-category-color-dot" style={{ background: catColor }} />
                              <span>{note.category || 'General'}</span>
                            </span>
                          </td>

                          <td>
                            <div className="note-table-tags">
                              {Array.isArray(note.tags) && note.tags.slice(0, 2).map((tg) => (
                                <span key={tg} className="note-tag-chip">
                                  #{tg}
                                </span>
                              ))}
                              {note.tags?.length > 2 && (
                                <span className="note-tag-more">+{note.tags.length - 2}</span>
                              )}
                            </div>
                          </td>

                          <td className="note-table-date">
                            {note.updated_at ? new Date(note.updated_at).toLocaleDateString() : 'Recent'}
                          </td>

                          <td>
                            <span className="zero-knowledge-pill">
                              <Shield size={11} color="#10b981" />
                              <span>AES-256</span>
                            </span>
                          </td>

                          <td onClick={(e) => e.stopPropagation()}>
                            <div className="note-menu-container">
                              <button
                                type="button"
                                className={`btn-icon btn-sm note-menu-trigger ${isMenuOpen ? 'active' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveMenuNoteId(isMenuOpen ? null : note.id);
                                }}
                                title="Note Actions"
                              >
                                <MoreVertical size={15} />
                              </button>

                              {isMenuOpen && (
                                <>
                                  <div
                                    className="note-menu-backdrop"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveMenuNoteId(null);
                                    }}
                                  />
                                  <div className="note-dropdown-menu glass-panel animate-scale-in" onClick={(e) => e.stopPropagation()}>
                                    <div className="note-menu-header">
                                      <div className="note-menu-title">{note.title || 'Untitled Note'}</div>
                                      <div className="note-menu-sub">{note.category || 'General'}</div>
                                    </div>
                                    <div className="note-menu-header-divider" />

                                    <button
                                      type="button"
                                      className="note-menu-item"
                                      onClick={() => {
                                        setActiveMenuNoteId(null);
                                        setViewingNote(note);
                                      }}
                                    >
                                      <FileText size={14} />
                                      <span>Open</span>
                                    </button>

                                    {canEdit && activeFilter !== 'trash' && (
                                      <button
                                        type="button"
                                        className="note-menu-item"
                                        onClick={() => {
                                          setActiveMenuNoteId(null);
                                          setEditingNote(note);
                                          setIsEditorOpen(true);
                                        }}
                                      >
                                        <Edit3 size={14} />
                                        <span>Edit</span>
                                      </button>
                                    )}

                                    <button
                                      type="button"
                                      className="note-menu-item"
                                      onClick={() => {
                                        setActiveMenuNoteId(null);
                                        handleToggleFavorite(note);
                                      }}
                                    >
                                      <Star size={14} />
                                      <span>{note.is_favorite ? 'Remove Favorite' : 'Add to Favorites'}</span>
                                    </button>

                                    {canCreate && activeFilter !== 'trash' && (
                                      <button
                                        type="button"
                                        className="note-menu-item"
                                        onClick={() => {
                                          setActiveMenuNoteId(null);
                                          handleDuplicateNote(note);
                                        }}
                                      >
                                        <Copy size={14} />
                                        <span>Duplicate</span>
                                      </button>
                                    )}

                                    {activeFilter !== 'trash' && (
                                      <>
                                        <button
                                          type="button"
                                          className="note-menu-item"
                                          onClick={() => {
                                            setActiveMenuNoteId(null);
                                            setMoveToCategoryTarget(note);
                                          }}
                                        >
                                          <Folder size={14} />
                                          <span>Move to Category</span>
                                        </button>

                                        <button
                                          type="button"
                                          className="note-menu-item"
                                          onClick={() => {
                                            setActiveMenuNoteId(null);
                                            setManageTagsTarget(note);
                                          }}
                                        >
                                          <Tag size={14} />
                                          <span>Manage Tags</span>
                                        </button>
                                      </>
                                    )}

                                    {canShare && activeFilter !== 'trash' && (
                                      <button
                                        type="button"
                                        className="note-menu-item"
                                        onClick={() => {
                                          setActiveMenuNoteId(null);
                                          setShareNoteTarget(note);
                                        }}
                                      >
                                        <Share2 size={14} />
                                        <span>Share</span>
                                      </button>
                                    )}

                                    <button
                                      type="button"
                                      className="note-menu-item"
                                      onClick={() => {
                                        setActiveMenuNoteId(null);
                                        handleCopyLink(note);
                                      }}
                                    >
                                      <Link2 size={14} />
                                      <span>Copy Link</span>
                                    </button>

                                    <button
                                      type="button"
                                      className="note-menu-item"
                                      onClick={() => {
                                        setActiveMenuNoteId(null);
                                        setActivityNote(note);
                                      }}
                                    >
                                      <History size={14} />
                                      <span>View Activity</span>
                                    </button>

                                    <div className="note-menu-divider" />

                                    {activeFilter === 'trash' ? (
                                      <>
                                        <button
                                          type="button"
                                          className="note-menu-item text-success"
                                          onClick={() => {
                                            setActiveMenuNoteId(null);
                                            handleRestoreFromTrash(note);
                                          }}
                                        >
                                          <RotateCcw size={14} />
                                          <span>Restore</span>
                                        </button>
                                        <button
                                          type="button"
                                          className="note-menu-item text-danger"
                                          onClick={() => {
                                            setActiveMenuNoteId(null);
                                            setPermanentDeleteTarget(note);
                                          }}
                                        >
                                          <Trash2 size={14} />
                                          <span>Permanent Delete</span>
                                        </button>
                                      </>
                                    ) : (
                                      canDelete && (
                                        <button
                                          type="button"
                                          className="note-menu-item text-danger"
                                          onClick={() => {
                                            setActiveMenuNoteId(null);
                                            handleMoveToTrash(note);
                                          }}
                                        >
                                          <Trash2 size={14} />
                                          <span>Move to Trash</span>
                                        </button>
                                      )
                                    )}

                                    <div className="note-menu-cancel-row">
                                      <button
                                        type="button"
                                        className="note-menu-cancel-btn"
                                        onClick={() => setActiveMenuNoteId(null)}
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── Modals ── */}

        {/* Note Editor Modal */}
        <SecureNoteEditorModal
          open={isEditorOpen}
          note={editingNote}
          categories={categories}
          onClose={() => {
            setIsEditorOpen(false);
            setEditingNote(null);
          }}
          onSave={handleSaveNote}
          hasEditPermission={canEdit}
          onOpenTemplates={() => setTemplatesModalOpen(true)}
        />

        {/* Note Viewer Modal */}
        <SecureNoteViewModal
          open={Boolean(viewingNote)}
          note={viewingNote}
          onClose={() => setViewingNote(null)}
          onEdit={(n) => {
            setEditingNote(n);
            setIsEditorOpen(true);
          }}
          onToggleFavorite={handleToggleFavorite}
          onDuplicate={handleDuplicateNote}
          onMoveToCategory={(n) => setMoveToCategoryTarget(n)}
          onManageTags={(n) => setManageTagsTarget(n)}
          onShare={(n) => setShareNoteTarget(n)}
          onViewActivity={(n) => setActivityNote(n)}
          onMoveToTrash={handleMoveToTrash}
          onUpdateContent={handleUpdateNoteContent}
          hasEditPermission={canEdit}
          hasDeletePermission={canDelete}
          hasSharePermission={canShare}
        />

        {/* Note Activity & Version History Modal */}
        <NoteActivityModal
          open={Boolean(activityNote)}
          note={activityNote}
          onClose={() => setActivityNote(null)}
          onRestoreVersion={handleRestoreVersion}
        />

        {/* Note Categories Management Modal */}
        <NoteCategoryModal
          open={categoryModalOpen}
          categories={categories}
          notes={notes}
          onClose={() => setCategoryModalOpen(false)}
          onUpdateCategories={handleUpdateCategories}
        />

        {/* Note Reusable Templates Modal */}
        <NoteTemplatesModal
          open={templatesModalOpen}
          onClose={() => setTemplatesModalOpen(false)}
          onSelectTemplate={(tpl) => {
            setEditingNote({
              title: tpl.defaultTitle,
              category: tpl.category,
              tags: tpl.tags,
              content: tpl.content,
              attachments: [],
              is_favorite: false,
              isLocked: false,
            });
            setIsEditorOpen(true);
          }}
        />

        {/* Move to Category Modal */}
        <MoveToCategoryModal
          open={Boolean(moveToCategoryTarget)}
          note={moveToCategoryTarget}
          categories={categories}
          onClose={() => setMoveToCategoryTarget(null)}
          onSave={handleSaveCategoryMove}
        />

        {/* Manage Tags Modal */}
        <ManageNoteTagsModal
          open={Boolean(manageTagsTarget)}
          note={manageTagsTarget}
          onClose={() => setManageTagsTarget(null)}
          onSave={handleSaveTags}
        />

        {/* Permanent Delete Modal */}
        <PermanentDeleteModal
          open={Boolean(permanentDeleteTarget)}
          note={permanentDeleteTarget}
          onClose={() => setPermanentDeleteTarget(null)}
          onConfirm={handlePermanentDelete}
        />

        {/* CipherVault Share Item Modal */}
        {shareNoteTarget && (
          <ShareVaultItemModal
            open={Boolean(shareNoteTarget)}
            item={{
              id: shareNoteTarget.id,
              name: shareNoteTarget.title,
              title: shareNoteTarget.title,
              item_type: 'SECURE_NOTE',
              encrypted_data: shareNoteTarget.encrypted_data,
            }}
            onClose={() => setShareNoteTarget(null)}
            onCreated={() => {
              showToast('Share grant created successfully');
              setShareNoteTarget(null);
            }}
          />
        )}
      </div>
    </MainLayout>
  );
}
